import { NavLink, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  ClickAwayListener,
  Collapse,
  Divider,
  IconButton,
  Link,
  Popper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { LeadQuickActions } from '../lead-modules/LeadQuickActions';
import useFetch from '../../../api/useFetch';
import ExoAvatar from '../../../components/exo/ExoAvatar';
import { tokens } from '../../../global/theme/tokens';
import { LeadStatusSelector } from '../status/LeadStatusSelector';
import { Status } from '../../../components/special/Status';
import { Fragment, useEffect, useRef, useState } from 'react';
import { API_PATCH, DBRequest, useConfig } from '../../../api/api';
import { useTranslation } from 'react-i18next';
import { LeadNameAddress } from '../lead-modules/LeadNameAddress';
import ExoDialog from '../../../components/exo/ExoDialog';
import EditIcon from '@mui/icons-material/Edit';
import LaunchIcon from '@mui/icons-material/Launch';
import { LeadMeetingHistory } from '../lead-modules/LeadMeetingHistory';
import Feedback from '../../../components/special/Feedback';
import { LeadInteractionHistory } from '../lead-modules/LeadInteractionHistory';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { NewInteraction } from '../../../components/new/NewInteraction';
import { NewInteractionNote } from '../../../components/new/NewInteractionNote';
import { updateOrCreate } from '../../../components/special/updateOrCreate';
import moment from 'moment';
import { ExoTasks } from '../../../components/exo/ExoTasks';
import { NewTask } from '../../../components/new/NewTask';
import { NewPersonLead } from '../../../components/new/NewPersonLead';
import { LeadProperties } from '../lead-modules/LeadProperties';
import { ImportantNote } from './ImportantNote';
import { validateLink } from '../../../components/special/validateLink';
import NewContact from '../../../components/new/NewContact';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddIcon from '@mui/icons-material/Add';
import { ExoSalesObjectMatching } from '../../../components/exo/ExoSalesObjectMatching';
import { SalesObjectMatch } from '../../../components/exo/sales-object-matching/match-preview/SalesObjectMatch';
import DateTimeLabel from '../../../components/label/DateTimeLabel';

export const ShowLeadsPersons = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const { t } = useTranslation();
  const { id } = useParams();

  const { apiData, setApiData } = useFetch(`leads/persons/${id}`);

  const [interactions, setInteractions] = useState(null);
  const [invitations, setInvitations] = useState(null);
  const [tasks, setTasks] = useState(null);

  const [emails, setEmails] = useState([]);
  const [telephoneNumbers, setTelephoneNumbers] = useState([]);

  const [openTopMatches, setOpenTopMatches] = useState(false);

  const {
    salutation,
    firstName,
    lastName,
    logo,
    address,
    status,
    employeeCount,
    url,
    note,
    responsibleUser,
    position,
    properties,
    companies,
    matches,
  } = apiData?.data || {};

  const name = `${salutation || ''} ${firstName || ''} ${lastName}`;

  useEffect(() => {
    // interactions
    DBRequest({
      config,
      path: `interactions?participantType[eq]=personLead&participantId[eq]=${id}`,
      onResponse: setInteractions,
    });

    // invitations
    DBRequest({
      config,
      path: `event-invitations?recipientType[eq]=personLead&recipientId[eq]=${id}`,
      onResponse: setInvitations,
    });

    // tasks
    loadUserTasks(0);
  }, [config, id]);

  function loadUserTasks(status) {
    DBRequest({
      config,
      path: `user-tasks?targetType[eq]=personLead&targetId[eq]=${id}&status[eq]=${status}`,
      onResponse: setTasks,
    });
  }

  const [anchorEl, setAnchorEl] = useState(null);
  const [itemToChange, setItemToChange] = useState(null);
  const handleClick = (event, item) => {
    setAnchorEl(anchorEl ? null : event?.currentTarget);
    setItemToChange(item);
  };
  function handleStatusChange(status, item) {
    DBRequest({
      config,
      path: `leads/persons/${item.id}`,
      method: API_PATCH({ ...item, status }),
      onResponse: setApiData,
    });
  }

  // edit
  const [openEdit, setOpenEdit] = useState(false);
  const [editInteraction, setEditInteraction] = useState(null);
  const [editTask, setEditTask] = useState(null);

  // feedback
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });

  function handleFeedback(text, type) {
    setAlertState({
      alertOpen: true,
      alertText: text,
      alertType: type,
    });
  }

  // meeting change
  function updateInviations() {
    DBRequest({
      config,
      path: `event-invitations?recipientType[eq]=personLead&recipientId[eq]=${id}`,
      onResponse: setInvitations,
    });
  }

  // task change
  function updateTasks(res) {
    const newTasks = updateOrCreate(tasks, res.data);
    setTasks(newTasks);
  }
  // interactions change
  function updateInteractions(res) {
    const newInteractions = updateOrCreate(interactions, res.data);
    const newSortedInteractions = sortInteractions(newInteractions);

    setInteractions(newSortedInteractions);
  }

  function sortInteractions(inter) {
    const sortedInteractions = inter.data.sort((a, b) =>
      moment(b.dateTime).diff(moment(a.dateTime))
    );
    return { ...inter, data: sortedInteractions };
  }

  function handleLoadInteractions(type) {
    setInteractions(null);
    const path = !type
      ? `interactions?participantType[eq]=personLead&participantId[eq]=${id}`
      : `interactions?participantType[eq]=personLead&participantId[eq]=${id}&type[eq]=${type}`;

    DBRequest({
      config,
      path,
      onResponse: setInteractions,
    });
  }

  const [openCompanies, setOpenCompanies] = useState(true);
  const [openLeads, setOpenLeads] = useState(true);

  useEffect(() => {
    calcEmailList();
    calcTelephoneNumbersList();
  }, [apiData?.data.emails, apiData?.data.persons]);

  function calcEmailList() {
    let emails = [];
    companies?.forEach((element) => {
      if (element.emails)
        element.emails.forEach((email) => {
          emails.push({
            ...email,
            emailType: `${element.nameShort || element.name} - ${
              email.emailType
            }`,
          });
        });
    });

    setEmails(emails);
  }

  function calcTelephoneNumbersList() {
    let telephoneNumbers = [];
    companies?.forEach((element) => {
      if (element.telephoneNumbers)
        element.telephoneNumbers.forEach((number) => {
          telephoneNumbers.push({
            ...number,
            phoneType: `${element.nameShort || element.name} - ${
              number.phoneType
            }`,
          });
        });
    });
    setTelephoneNumbers(telephoneNumbers);
  }

  // lead to customer
  const [openLeadToCustomer, setOpenLeadToCustomer] = useState(false);
  function handleCreatedCustomer(res) {
    setApiData({ ...apiData, data: { ...apiData.data, contact: res.data } });
  }
  const contact = apiData?.data.contact;

  // matches Sales Objects

  const matchesCount = matches
    ? matches?.filter((match) => match?.element && match?.accessPoint).length
    : 0;

  const [currentType, setCurrentType] = useState(0);

  function handleTypeChange(e) {
    if (e.target.value === currentType) return;
    setCurrentType(e.target.value);
  }

  return (
    <Fragment key={id}>
      <Box className="pt-0 md:pt-10 flex flex-col xl:flex-row gap-2 xl:h-full md:overflow-y-auto xl:overflow-hidden w-full">
        <Box
          sx={{ bgcolor: colors.glass }}
          className=" rounded-lg backdrop-blur-md p-4 relative xl:min-w-[400px] xl:max-w-[450px] overflow-y-auto md:h-full lg:h-auto md:min-h-full lg:min-h-auto"
        >
          <IconButton
            sx={{ position: 'absolute' }}
            className="top-2 right-2"
            onClick={() => setOpenEdit(true)}
          >
            <EditIcon />
          </IconButton>
          <LeadNameAddress
            name={name}
            subName={position}
            address={address}
            image={logo}
            telephoneNumbers={apiData?.data.telephoneNumbers.concat(
              telephoneNumbers
            )}
            emails={apiData?.data.emails.concat(emails)}
            sourceType="personLead"
            sourceId={id}
            source={apiData?.data}
            onNewInteraction={updateInteractions}
          />
          <Box className="pt-2 pb-3">
            <Divider />
          </Box>
          <Box className="pb-2 w-full">
            <Typography fontWeight={600}>{t('Lead Status')}</Typography>
            <Status
              text={status?.label || t('No Status')}
              variant={status?.variant || 'base'}
              statusColor={status?.color}
              onClick={(e) => handleClick(e, apiData?.data)}
              disableMobile
              fullWidth
            />
          </Box>
          <LeadQuickActions
            isLoading={!apiData}
            name={name}
            data={apiData}
            type="personLead"
            typePlural="personLeads"
            onFeedback={handleFeedback}
            onMeetingChange={updateInviations}
            onInteractionChange={updateInteractions}
            onTaskChange={updateTasks}
          />
          {contact ? (
            <Box className="pt-[10px]">
              <Typography fontWeight={500} className="opacity-50">
                {t('Contact')}
              </Typography>
              <NavLink to={`/contacts/${contact.id}`}>
                <Box className="flex items-center gap-2">
                  <ExoAvatar
                    size="40px"
                    name={`${contact?.firstName ?? ''} ${contact?.lastName ?? ''}`}
                    color={contact?.color}
                    type="contact"
                  />
                  <Typography>{`${contact?.salutation ?? ''} ${contact?.firstName ?? ''} ${contact?.lastName ?? ''}`}</Typography>
                </Box>
              </NavLink>{' '}
            </Box>
          ) : (
            <Box className="w-full pt-[10px]">
              <Button
                className="w-full"
                onClick={() => setOpenLeadToCustomer(true)}
                sx={{
                  bgcolor: colors.card,
                  ':hover': { bgcolor: colors.selected },
                }}
              >
                {t('Convert to customer')}
                <AutoAwesomeIcon
                  className="ml-[5px]"
                  sx={{ fontSize: '16px' }}
                />
              </Button>
            </Box>
          )}
          <Box className="py-3">
            <Divider />
          </Box>
          <Box className="w-full grid grid-cols-2 gap-2">
            <Box>
              <Typography fontWeight={600}>{t('Info')}</Typography>
              <Typography
                className="pb-1 pl-2"
                sx={{ wordBreak: 'break-word' }}
              >
                {url && (
                  <a href={validateLink(url)} target="_blank">
                    <LaunchIcon className="mr-2" />
                    {url}
                  </a>
                )}
              </Typography>
            </Box>
          </Box>
          {companies && companies.length > 0 && (
            <Box className="flex flex-col gap-1 pb-2">
              <Button
                sx={{ padding: 0, justifyContent: 'space-between' }}
                onClick={() => setOpenCompanies(!openCompanies)}
              >
                <Typography fontWeight={600} className="pb-1">
                  {`${t('Company Leads')} (${companies.length})`}
                </Typography>
                <ArrowDropDownIcon
                  sx={{
                    transform: `rotate(${openCompanies ? 180 : 0}deg)`,
                  }}
                />
              </Button>
              <Collapse in={openCompanies}>
                <Box className="flex flex-col gap-1">
                  {companies?.map((company) => (
                    <NavLink
                      to={`/leads/companies/${company?.id}`}
                      key={company.id}
                    >
                      <Box className=" pl-2 flex flex-row justify-start items-center gap-2 whitespace-nowrap">
                        <ExoAvatar
                          name={company.nameShort || company.name}
                          size="35px"
                        />
                        <Typography>
                          {company.nameShort || company.name}
                        </Typography>
                      </Box>
                    </NavLink>
                  ))}
                </Box>
              </Collapse>
            </Box>
          )}

          <Box className="flex flex-col gap-1 pb-2">
            <Button
              sx={{ padding: 0, justifyContent: 'space-between' }}
              onClick={() => setOpenLeads(!openLeads)}
            >
              <Typography fontWeight={600} className="pb-1">
                {`${t('Sales Objects')} (${matchesCount})`}
              </Typography>
              <ArrowDropDownIcon
                sx={{
                  transform: `rotate(${openLeads ? 180 : 0}deg)`,
                }}
              />
            </Button>
            <Collapse in={openLeads}>
              <Box className="flex flex-col gap-1">
                {matches?.map((match) => {
                  /*if (!match?.element || !match?.accessPoint) {
                    // Skip rendering if either element or accessPoint is missing
                    return null;
                  }*/

                  if (match?.status == 2 && match?.element)
                    return (
                      <>
                        <NavLink
                          to={`/sales-objects/${match.element.id}`}
                          key={match.element.id}
                        >
                          <Box className="pl-2 flex flex-row justify-start items-center gap-2 whitespace-nowrap">
                            <ExoAvatar
                              picture={match.element.image?.temporaryUrl}
                              name={`${match.element.lastName} ${match.element.firstName}`}
                              size="35px"
                            />
                            <Box>
                              <Typography>{match.element.title}</Typography>
                              <Typography
                                fontSize={10}
                                className="opacity-80"
                                color="error"
                              >
                                {t('Aborted')}
                              </Typography>
                            </Box>
                          </Box>
                        </NavLink>
                      </>
                    );

                  if (
                    match?.status == 1 &&
                    match?.element &&
                    match?.accessPoint
                  )
                    return (
                      <>
                        <NavLink
                          to={`/sales-objects/${match.element.id}`}
                          key={match.element.id}
                        >
                          <Box className="flex justify-between">
                            <Box className="pl-2 flex flex-row justify-start items-center gap-2 whitespace-nowrap">
                              <ExoAvatar
                                picture={match.element.image?.temporaryUrl}
                                name={`${match.element.lastName} ${match.element.firstName}`}
                                size="35px"
                              />
                              <Box>
                                <Typography>{match.element.title}</Typography>
                                <Typography
                                  fontSize={10}
                                  className="opacity-60"
                                >
                                  {`${match.accessPoint.accessLevel} - ${match?.accessPoint?.level?.title}`}
                                </Typography>
                              </Box>
                            </Box>
                            {match?.accessPoint?.levelUpdatedAt && (
                              <Tooltip title={t('On this level since')}>
                                <Typography
                                  className="opacity-50"
                                  fontSize={11}
                                >
                                  <DateTimeLabel
                                    dateTime={
                                      match?.accessPoint?.levelUpdatedAt
                                    }
                                  />
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>
                        </NavLink>
                      </>
                    );

                  return;
                })}
                <Tooltip title={t('Explore Top Matches')}>
                  <Box className={' flex rounded-lg '}>
                    <Button
                      onClick={() => setOpenTopMatches(true)}
                      className=" opacity-40 w-full flex "
                      variant="contained"
                      color="secondary"
                      disableElevation
                    >
                      <AddIcon
                        className=" opacity-30"
                        sx={{
                          fontSize: '20px',
                          marginRight: 'Top Matches' ? '10px' : '0px',
                        }}
                      />
                    </Button>
                  </Box>
                </Tooltip>
              </Box>
            </Collapse>
          </Box>

          <ExoDialog
            open={openTopMatches}
            onClose={() => setOpenTopMatches(false)}
            limitWidth
          >
            <Box className="w-full max-w-[800px] h-full flex flex-col">
              <Box className="flex items-center justify-between w-full mb-2 relative">
                <Typography variant="h4" className="pb-1">
                  {t('Matches')}
                </Typography>
                <ToggleButtonGroup
                  sx={{
                    '& button': {
                      padding: '3px 6px',
                    },
                  }}
                  size="small"
                  value={currentType}
                  exclusive
                  onChange={handleTypeChange}
                  aria-label="Matching Status"
                  className="backdrop-blur-lg"
                >
                  <ToggleButton value={0}>{t('Matches')}</ToggleButton>
                  <ToggleButton value={1}>{t('Active')}</ToggleButton>
                  <ToggleButton value={2}>{t('Declined')}</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Typography className="pb-4 opacity-70">
                {t(
                  'A list of all hits that were determined based on the purchasing parameters stored in Leads'
                )}
              </Typography>
              <ExoSalesObjectMatching
                source="personLead"
                sourceId={id}
                status={currentType}
              />
            </Box>
          </ExoDialog>

          <ImportantNote
            apiData={apiData}
            setApiData={setApiData}
            path={`leads/persons/${id}`}
          />

          <LeadProperties properties={properties} />
          {responsibleUser && (
            <Box className="pt-10">
              <Typography fontWeight={600}>
                {t('Responsible Employee')}
              </Typography>
              <NavLink to={`/employees/${responsibleUser?.id}`}>
                <Box className=" pl-2 flex flex-row justify-start items-center gap-2 whitespace-nowrap">
                  <ExoAvatar
                    picture={responsibleUser?.picture?.thumbnailUrl}
                    name={`${responsibleUser?.lastName} ${responsibleUser?.firstName}`}
                    size="35px"
                  />
                  {`${responsibleUser?.salutation ?? ''} ${responsibleUser?.lastName ?? ''} ${responsibleUser?.firstName ?? ''}`}
                </Box>
              </NavLink>
            </Box>
          )}
        </Box>
        <Box
          sx={{ bgcolor: colors.glass }}
          className=" rounded-lg backdrop-blur-md pl-3 pr-2 py-2 relative w-full xl:h-full flex flex-col min-h-[200px]"
        >
          <Typography fontWeight={600} className="pb-2">
            {t('History')}
          </Typography>
          <LeadInteractionHistory
            leadType="personLead"
            leadId={id}
            interactions={interactions}
            onEdit={(e) => setEditInteraction(e)}
            setInteractions={setInteractions}
            onFeedback={handleFeedback}
            loadData={handleLoadInteractions}
          />
        </Box>
        <Box className="w-full grid grid-rows-2 gap-2">
          <Box
            sx={{ bgcolor: colors.glass }}
            className=" rounded-lg backdrop-blur-md pl-3 pr-2 py-2 relative w-full xl:h-full flex flex-col min-h-[200px]"
          >
            <Typography fontWeight={600} className="pb-4">
              {t('Tasks')} {tasks && `(${tasks.data.length})`}
            </Typography>
            <Box className="flex flex-col gap-1 overflow-y-auto max-h-[500px] md:max-h-[9999px] md:h-full">
              <ExoTasks
                tasks={tasks}
                onChange={setTasks}
                noNew
                toggelSx={{ position: 'absolute', top: 6, right: 6 }}
                onEdit={setEditTask}
                onStatusChange={loadUserTasks}
                onFeedback={handleFeedback}
                targetId={apiData?.data.id}
                targetType="personLead"
              />
            </Box>
          </Box>

          <Box
            sx={{ bgcolor: colors.glass }}
            className=" rounded-lg backdrop-blur-md pl-3 pr-2 py-2 relative w-full xl:h-full flex flex-col min-h-[200px]"
          >
            <Typography fontWeight={600} className="pb-2">
              {t('Meetings')}
            </Typography>
            <LeadMeetingHistory
              invitations={invitations}
              setInvitations={setInvitations}
            />
          </Box>
        </Box>
      </Box>

      <ExoDialog open={openEdit} onClose={() => setOpenEdit(false)} limitWidth>
        <NewPersonLead
          startData={apiData?.data}
          onUpdate={setApiData}
          onCreate={setApiData}
          onClose={() => setOpenEdit(false)}
          onFeedback={handleFeedback}
        />
      </ExoDialog>

      <ExoDialog
        open={editInteraction && editInteraction.type !== 'note'}
        onClose={() => setEditInteraction(null)}
        limitWidth
      >
        <NewInteraction
          startData={editInteraction}
          onClose={() => setEditInteraction(null)}
          onCreate={updateInteractions}
          onUpdate={updateInteractions}
          onFeedback={handleFeedback}
          textBlockType={'lead'}
        />
      </ExoDialog>

      <ExoDialog
        open={editInteraction && editInteraction.type === 'note'}
        onClose={() => setEditInteraction(null)}
        limitWidth
      >
        <NewInteractionNote
          startData={editInteraction !== 'note' ? editInteraction : {}}
          onClose={() => setEditInteraction(null)}
          onCreate={updateInteractions}
          onUpdate={updateInteractions}
          onFeedback={handleFeedback}
          textBlockType={'lead'}
        />
      </ExoDialog>

      <ExoDialog
        open={Boolean(editTask)}
        onClose={() => setEditTask(null)}
        limitWidth
      >
        <NewTask
          startData={editTask}
          onClose={() => setEditTask(null)}
          onCreate={updateTasks}
          onChange={updateTasks}
          onFeedback={handleFeedback}
          targetId={apiData?.data.id}
          targetType={'personLead'}
        />
      </ExoDialog>

      <ExoDialog
        open={openLeadToCustomer}
        onClose={() => setOpenLeadToCustomer(false)}
        limitWidth
      >
        <NewContact
          fullWidth
          startData={{
            ...apiData?.data,
            id: undefined,
            leadId: apiData?.data.id,
          }}
          onCreate={handleCreatedCustomer}
          onUpdate={handleCreatedCustomer}
          onClose={() => setOpenLeadToCustomer(false)}
        />
      </ExoDialog>

      <LeadStatusSelector
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onStatusChange={(status) =>
          handleStatusChange(status, {
            id: itemToChange.id,
          })
        }
      />
      <Feedback setState={setAlertState} state={alertState} />
    </Fragment>
  );
};
